import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import HamburgerIcon from './icons/Hamburger';
import MenuItem from './menu-item';
import { Organisation } from '@finn-no/organisation-http-client';

export type DropdownMenuItemProps = {
    header: React.ReactNode;
    children: React.ReactNode;
    rightAlign?: boolean;
};
function DropdownMenuItem({
    header,
    children,
    rightAlign = false,
}: DropdownMenuItemProps) {
    const [selected, setSelected] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        function handleClickOutside(event: Event) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setSelected(false);
            }
        }
        document.addEventListener('mousemove', handleClickOutside);
        document.addEventListener('keydown', handleClickOutside);
        return () => {
            document.removeEventListener('mousemove', handleClickOutside);
            document.removeEventListener('keydown', handleClickOutside);
        };
    }, [ref]);

    let contentClasses =
        'top-bar__dropdown-menu-item__content u-caption u-border-radius-4px';
    if (rightAlign) {
        contentClasses += ' top-bar__dropdown-menu-item__content-right';
    }
    if (selected) {
        contentClasses += ' top-bar__dropdown-menu-item__content-open';
    }
    const menuItemClass = selected
        ? 'top-bar__dropdown-menu-item top-bar__dropdown-menu-item__selected'
        : 'top-bar__dropdown-menu-item';

    return (
        <div ref={ref} className={menuItemClass}>
            <button
                type="button"
                className="top-bar__dropdown-menu-item__header"
                onClick={() => setSelected(!selected)}
                aria-expanded={selected}
            >
                {header}
            </button>
            <div className={contentClasses}>{children}</div>
        </div>
    );
}

export type DropdownMenuProps = {
    activeOrganisation: Organisation;
    track: (name: string) => void;
    isHamburgerMenuOpen: boolean;
    handleHamburgerMenuToggle: () => void;
    isCompanyProfileOnJobUnleashEnabled: boolean;
    isCompanyProfileOnMotorUnleashEnabled: boolean;
    isMobilityPriceoverviewRedirectUnleashEnabled?: boolean;
};

export default function DropdownMenu(props: DropdownMenuProps) {
    const {
        activeOrganisation,
        track,
        isHamburgerMenuOpen,
        handleHamburgerMenuToggle,
        isCompanyProfileOnJobUnleashEnabled,
        isCompanyProfileOnMotorUnleashEnabled,
        isMobilityPriceoverviewRedirectUnleashEnabled,
    } = props;

    const menuClass = isHamburgerMenuOpen
        ? 'top-bar__dropdown-menu-active'
        : 'top-bar__dropdown-menu';
    const hammburgerMenuClasses = isHamburgerMenuOpen
        ? 'top-bar__menu-item top-bar__menu-icon top-bar__menu-icon-active'
        : 'top-bar__menu-item top-bar__menu-icon';

    const priceOverviewUrl =
        activeOrganisation.market === 'MOTOR' &&
        isMobilityPriceoverviewRedirectUnleashEnabled
            ? `/bedrift/mobility/prisoversikt?orgId=${activeOrganisation.orgId}`
            : `/bedrift/prisoversikt?orgId=${activeOrganisation.orgId}`;

    const companyProfileLinks = (
        <>
            <a
                className="link"
                onClick={() => track('profile-click')}
                href={`/basic-profile?orgId=${activeOrganisation.orgId}`}
            >
                Basisprofil
            </a>
            <a
                className="link"
                onClick={() => track('profile-click')}
                href={`/brand-profile/extended-profile?orgId=${activeOrganisation.orgId}`}
            >
                Tydelig profil
            </a>
        </>
    );

    return (
        <>
            <span className={menuClass}>
                <DropdownMenuItem header="Annonser">
                    <a
                        className="link"
                        onClick={() => track('new-ad-button-click')}
                        href={`/bedrift/ny-annonse?orgId=${activeOrganisation.orgId}`}
                    >
                        Legg ut annonse
                    </a>
                    <a
                        className="link"
                        onClick={() => track('all-ads-button-click')}
                        href={`/bedrift/mine-annonser?orgId=${activeOrganisation.orgId}`}
                    >
                        Alle annonser
                    </a>
                </DropdownMenuItem>
                <DropdownMenuItem header="Betaling">
                    <a
                        className="link"
                        onClick={() => track('order-payment-button-click')}
                        href={`/bedrift/ordre-og-betaling/ordre?orgId=${activeOrganisation.orgId}`}
                    >
                        Ordre
                    </a>
                    <a
                        className="link"
                        onClick={() => track('invoices-button-click')}
                        href={`/bedrift/ordre-og-betaling/betaling?orgId=${activeOrganisation.orgId}`}
                    >
                        Fakturaer
                    </a>
                    <a
                        className="link"
                        onClick={() => track('pricelist-button-click')}
                        href={priceOverviewUrl}
                    >
                        Prisoversikt
                    </a>
                </DropdownMenuItem>
                {activeOrganisation.market === 'JOB' && (
                    <DropdownMenuItem header="Verktøy">
                        <a
                            className="link"
                            onClick={() => track('job-tips-button-click')}
                            href="/bedriftskunde/jobb"
                            target="_blank"
                        >
                            Tips & info
                        </a>
                        <a
                            className="link"
                            onClick={() =>
                                track('schibsted-ad-school-button-click')
                            }
                            href="https://www.youtube.com/FINNnoforbedrifter"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Opplæringsfilmer
                        </a>
                    </DropdownMenuItem>
                )}
                {activeOrganisation.market === 'MOTOR' && (
                    <DropdownMenuItem header="Verktøy">
                        <a
                            className="link"
                            onClick={() => track('motor-tips-button-click')}
                            href="/bedriftskunde/motor"
                            target="_blank"
                        >
                            Tips & info
                        </a>
                    </DropdownMenuItem>
                )}
                <DropdownMenuItem header="Innstillinger" rightAlign>
                    <a
                        className="link"
                        onClick={() => track('users-button-click')}
                        href={`/bedrift/brukere?orgId=${activeOrganisation.orgId}`}
                    >
                        Brukere
                    </a>
                    <a
                        className="link"
                        onClick={() => track('invoice-settings-button-click')}
                        href={`/bedrift/innstillinger?orgId=${activeOrganisation.orgId}`}
                    >
                        Fakturainnstillinger
                    </a>
                    {activeOrganisation.market === 'JOB' && (
                        <a
                            className="link"
                            onClick={() => track('business-profile-click')}
                            href={`/bedrift/bedriftsprofil?orgId=${activeOrganisation.orgId}`}
                        >
                            Bedriftsprofil
                        </a>
                    )}
                    {activeOrganisation.market === 'JOB' &&
                        isCompanyProfileOnJobUnleashEnabled && (
                            <>{companyProfileLinks}</>
                        )}
                    {activeOrganisation.market === 'MOTOR' &&
                        isCompanyProfileOnMotorUnleashEnabled && (
                            <>{companyProfileLinks}</>
                        )}
                </DropdownMenuItem>
            </span>
            <MenuItem
                className={hammburgerMenuClasses}
                icon={<HamburgerIcon />}
                onClick={handleHamburgerMenuToggle}
            />
        </>
    );
}

DropdownMenu.defaultProps = {
    track: () => undefined,
    isHamburgerMenuOpen: false,
    handleHamburgerMenuToggle: () => undefined,
    isCompanyProfileOnJobUnleashEnabled: false,
    isCompanyProfileOnMotorUnleashEnabled: false,
    isMobilityPriceoverviewRedirectUnleashEnabled: false,
    activeAccount: {},
};

DropdownMenu.propTypes = {
    track: PropTypes.func,
    activeAccount: PropTypes.shape({
        market: PropTypes.string,
        orgId: PropTypes.number,
    }),
    isHamburgerMenuOpen: PropTypes.bool,
    handleHamburgerMenuToggle: PropTypes.func,
    isCompanyProfileOnJobUnleashEnabled: PropTypes.bool,
    isCompanyProfileOnMotorUnleashEnabled: PropTypes.bool,
    isMobilityPriceoverviewRedirectUnleashEnabled: PropTypes.bool,
};
